import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Button,
  Divider,
  ScrollArea,
  Skeleton,
  Tabs,
  TextInput
} from '@mantine/core';
import { AlertCircle, ChevronRight } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { getInitialsFromName } from '../../../helpers/format';
import AppFlexbox from '../../common/AppFlexbox';
import AppCheckbox from '../../common/AppCheckbox';
import AppCard from '../../common/AppCard';

const StoreCreationTeamSelectStep = ({
  loading,
  createOptions,
  formState,
  setFormState
}) => {
  const [filterState, setFilterState] = useState({
    activeTab: 'teams',
    search: ''
  });

  const regAssociationTeams =
    createOptions?.regAssociations.reduce((r, c) => {
      c.teams.forEach((t) => {
        if (!r.some((x) => x.pkTeam === t.pkTeam)) {
          r.push({
            ...t,
            pkRegAssociation: c.pkRegAssociation,
            associationName: c.name
          });
        }
      });
      return r;
    }, []) || [];
  const personalTeams = createOptions?.teams || [];
  const filteredTeams = (filterState.activeTab === 'association'
    ? regAssociationTeams
    : personalTeams
  ).filter(
    (t) =>
      !filterState.search ||
      t.name.toLowerCase().includes(filterState.search.toLowerCase())
  );

  useEffect(() => {
    setFilterState({ activeTab: 'teams', search: '' });
  }, []);

  const getStoreSettingsForTeam = (team) => {
    const regAssociation =
      createOptions.regAssociations.find(
        (a) => a.pkRegAssociation === team.pkRegAssociation
      ) || null;

    const settings = {
      name: team.name,
      primaryColor:
        team.primaryColor || regAssociation?.primaryColor || '#000000',
      secondaryColor:
        team.secondaryColor || regAssociation?.secondaryColor || '#FFFFFF',
      supportEmail: regAssociation?.supportEmail || '',
      logoImageUrl: team.image || regAssociation?.logoImageUrl || '',
      bannerImageUrl: regAssociation?.heroImageUrl || ''
    };

    return { settings, regAssociation };
  };

  const onSelectTeam = (team) => {
    const { settings, regAssociation } = getStoreSettingsForTeam(team);
    setFormState({
      ...formState,
      copyThemeFromStockContent: false,
      storeSettings: {
        ...formState.storeSettings,
        ...settings
      },
      copyContent: {
        ...formState.copyContent,
        copyBranding: false
      },
      team: {
        ...team,
        regAssociation
      }
    });
  };

  const onUnselectTeam = () => {
    if (formState.team) {
      const { settings } = getStoreSettingsForTeam(formState.team);
      setFormState({
        ...formState,
        copyThemeFromStockContent: true,
        copyContent: {
          ...formState.copyContent,
          copyBranding: true
        },
        team: null,
        storeSettings: {
          ...formState.storeSettings,
          name:
            formState.storeSettings.name === settings.name
              ? ''
              : formState.storeSettings.name,
          primaryColor:
            formState.storeSettings.primaryColor === settings.primaryColor
              ? '#000000'
              : formState.storeSettings.primaryColor,
          secondaryColor:
            formState.storeSettings.secondaryColor === settings.secondaryColor
              ? '#FFFFFF'
              : formState.storeSettings.secondaryColor,
          logoImageUrl:
            formState.storeSettings.logoImageUrl === settings.logoImageUrl
              ? ''
              : formState.storeSettings.logoImageUrl,
          bannerImageUrl:
            formState.storeSettings.bannerImageUrl === settings.bannerImageUrl
              ? ''
              : formState.storeSettings.bannerImageUrl
        }
      });
    }
  };

  return (
    <AppStack style={{ gap: 10, paddingBottom: 10 }}>
      {formState.team ? (
        <AppStack style={{ gap: 20 }}>
          <Button
            color="dark"
            onClick={onUnselectTeam}
            radius={8}
            shadow="sm"
            style={{
              padding: 0,
              minHeight: 50,
              width: '100%',
              flex: 1
            }}
            styles={{ label: { flex: 1 } }}
            variant="filled"
          >
            <AppFlexbox
              style={{
                flex: 1,
                padding: 12,
                gap: 0,
                justifyContent: 'space-between'
              }}
            >
              <AppFlexbox
                style={{
                  flex: 1,
                  gap: 16,
                  alignItems: 'center'
                }}
              >
                <Avatar
                  color="dark"
                  radius="xl"
                  size="md"
                  src={formState.team.image}
                >
                  <AppText style={{ fontWeight: 500 }}>
                    {getInitialsFromName(formState.team.name)}
                  </AppText>
                </Avatar>

                <AppStack style={{ flex: 1, gap: 0, alignItems: 'start' }}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {formState.team.name}
                  </AppText>
                  <AppText style={{ fontSize: 14, color: '#FFF' }}>
                    {formState.team.associationName}
                  </AppText>
                </AppStack>
              </AppFlexbox>

              <AppStack style={{ justifyContent: 'center' }}>
                <AppCheckbox checked color="dark" onChange={() => {}} />
              </AppStack>
            </AppFlexbox>
          </Button>
          <AppStack style={{ alignItems: 'center', gap: 10 }}>
            <AppStack style={{ gap: 0, padding: '0px 40px' }}>
              <AppText style={{ fontSize: 14, textAlign: 'center' }}>
                Are you sure you want to select this team for the store?
              </AppText>

              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                You can always change it later in store settings.
              </AppText>
            </AppStack>
            <Divider
              label="OR"
              labelPosition="center"
              style={{ width: '100%' }}
            />

            <Button
              color="dark"
              onClick={onUnselectTeam}
              radius={8}
              shadow="sm"
              variant="outline"
            >
              Choose a different team
            </Button>
          </AppStack>
        </AppStack>
      ) : (
        <AppStack
          style={{
            flex: 1,
            gap: 10,
            overflow: 'hidden',
            height: '100%'
          }}
        >
          <AppStack style={{ gap: 0 }}>
            <Tabs
              onChange={(activeTab) =>
                setFilterState({ ...filterState, activeTab })
              }
              value={filterState.activeTab}
            >
              <Tabs.List>
                {loading ? (
                  <>
                    <Tabs.Tab color="dark" value="teams">
                      <Skeleton height={16} width={80} />
                    </Tabs.Tab>
                    <Tabs.Tab color="dark" value="association">
                      <Skeleton height={16} width={120} />
                    </Tabs.Tab>
                  </>
                ) : (
                  <>
                    <Tabs.Tab color="dark" value="teams">
                      <AppText
                        style={{
                          fontSize: 14,
                          fontWeight:
                            filterState.activeTab === 'teams' ? 500 : 'normal'
                        }}
                      >
                        Your teams
                      </AppText>
                    </Tabs.Tab>
                    {regAssociationTeams.length > 0 && (
                      <Tabs.Tab color="dark" value="association">
                        <AppText
                          style={{
                            fontSize: 14,
                            fontWeight:
                              filterState.activeTab === 'association'
                                ? 500
                                : 'normal'
                          }}
                        >
                          Association teams
                        </AppText>
                      </Tabs.Tab>
                    )}
                  </>
                )}
              </Tabs.List>
            </Tabs>

            <TextInput
              disabled={
                loading ||
                (personalTeams.length === 0 &&
                  filterState.activeTab === 'teams') ||
                (regAssociationTeams.length === 0 &&
                  filterState.activeTab === 'association')
              }
              onChange={(e) =>
                setFilterState({
                  ...filterState,
                  search: e.currentTarget.value
                })
              }
              placeholder="Search teams by name"
              size="sm"
              style={{ marginTop: 5 }}
              value={filterState.search}
            />
          </AppStack>
          <AppStack
            style={{
              flex: 1,
              border: 'solid 1px lightgrey',
              padding: 5,
              borderRadius: 5,
              height: '100%',
              overflow: 'hidden'
            }}
          >
            {loading ? (
              <AppStack style={{ gap: 2 }}>
                {Array(2)
                  .fill()
                  .map((_, s) => (
                    <AppCard
                      // eslint-disable-next-line react/no-array-index-key
                      key={s}
                      radius={8}
                      shadow="sm"
                      style={{ padding: 0 }}
                      withBorder
                    >
                      <AppFlexbox
                        style={{
                          minHeight: 40,
                          flex: 1,
                          padding: 12,
                          alignItems: 'center'
                        }}
                      >
                        <Skeleton height={30} radius={30} width={30} />
                        <AppStack style={{ flex: 1, gap: 2 }}>
                          <Skeleton height={10} mb={6} width="50%" />
                          <Skeleton height={10} width="40%" />
                        </AppStack>
                      </AppFlexbox>
                    </AppCard>
                  ))}
              </AppStack>
            ) : filteredTeams.length === 0 ? (
              <AppStack
                style={{
                  padding: 20,
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 5
                }}
              >
                <AlertCircle color="#000" size={30} />
                <AppText style={{ fontSize: 16, color: '#000' }}>
                  No teams found
                </AppText>

                {filterState.search && (
                  <Button
                    color="dark"
                    onClick={() => {
                      setFilterState({ ...filterState, search: '' });
                    }}
                    variant="outline"
                  >
                    Clear search
                  </Button>
                )}
              </AppStack>
            ) : (
              <ScrollArea.Autosize
                style={{ width: '100%', height: '100%', maxHeight: 600 }}
              >
                {filteredTeams
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((t) => (
                    <Button
                      key={t.pkTeam}
                      color="dark"
                      onClick={() => {
                        onSelectTeam(t);
                      }}
                      radius={8}
                      shadow="sm"
                      style={{
                        padding: 0,
                        minHeight: 50,
                        width: '100%',
                        flex: 1
                      }}
                      styles={{ label: { flex: 1 } }}
                      variant="subtle"
                    >
                      <AppFlexbox
                        style={{
                          flex: 1,
                          padding: 12,
                          gap: 0,
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppFlexbox
                          style={{
                            flex: 1,
                            gap: 16,
                            alignItems: 'center'
                          }}
                        >
                          <Avatar
                            color="dark"
                            radius="xl"
                            size="md"
                            src={t.image}
                          >
                            <AppText style={{ fontWeight: 500 }}>
                              {getInitialsFromName(t.name)}
                            </AppText>
                          </Avatar>

                          <AppStack
                            style={{ flex: 1, gap: 0, alignItems: 'start' }}
                          >
                            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                              {t.name}
                            </AppText>
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              {t.associationName}
                            </AppText>
                          </AppStack>
                        </AppFlexbox>

                        <AppStack style={{ justifyContent: 'center' }}>
                          <ChevronRight size={18} />
                        </AppStack>
                      </AppFlexbox>
                    </Button>
                  ))}
              </ScrollArea.Autosize>
            )}
          </AppStack>
        </AppStack>
      )}
    </AppStack>
  );
};

StoreCreationTeamSelectStep.propTypes = {
  createOptions: PropTypes.object,
  formState: PropTypes.object,
  loading: PropTypes.bool,
  setFormState: PropTypes.func
};

export default StoreCreationTeamSelectStep;
